import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import CheckIcon from '@mui/icons-material/Check';
import { api } from '@/api/repository/axios';
import type { SubwayStation } from '@/api/repository/types/types';
import { designSystem } from '@/styles/theme';

/**
 * 역 이름 검색 공용 조각 — 검색 상태(useStationSearch) + 결과 줄(StationResultList).
 * 폼의 [가까운 지하철역](StationSearchField 시트)과 채용 목록 지하철역 필터 시트가 같이 쓴다.
 */

const COLOR = {
  text: '#1F2A28',
  muted: '#66736F',
  lineSoft: '#F0F3F2',
  primary: designSystem.colors.primary, // #5A9B96
  primaryDeep: '#2F6B66',
  primarySoft: '#E6F1EF',
} as const;

/** 입력이 멈추고 이만큼 지나면 검색한다 */
const SEARCH_DELAY_MS = 200;
const SEARCH_LIMIT = 30;

export interface StationSearchState {
  results: SubwayStation[];
  /** 결과 대신 보일 안내(빈 입력·찾는 중·실패·없음). null 이면 결과를 그린다 */
  status: string | null;
  /** 새 검색어로 찾는 중(직전 결과는 그대로 둔다) */
  loading: boolean;
}

/**
 * 입력이 멈추고 200ms 뒤 검색, 요청이 겹치면 마지막 요청의 결과만 쓴다.
 * 새 검색어의 결과가 오기 전에는 직전 결과를 그대로 두고 '찾는 중'만 알린다.
 */
export const useStationSearch = (query: string): StationSearchState => {
  const [results, setResults] = useState<SubwayStation[]>([]);
  const [searchedQuery, setSearchedQuery] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const requestSeq = useRef<number>(0);
  const trimmed: string = query.trim();

  useEffect(() => {
    const seq: number = ++requestSeq.current;
    if (!trimmed) {
      setResults([]);
      setSearchedQuery('');
      setLoading(false);
      setFailed(false);
      return undefined;
    }
    setLoading(true);
    const timer: number = window.setTimeout(async () => {
      try {
        const res = await api.search.searchSubwayStations(trimmed, SEARCH_LIMIT);
        if (seq !== requestSeq.current) return;
        setResults(Array.isArray(res?.result) ? res.result : []);
        setFailed(false);
      } catch {
        if (seq !== requestSeq.current) return;
        setResults([]);
        setFailed(true);
      } finally {
        if (seq === requestSeq.current) {
          setSearchedQuery(trimmed);
          setLoading(false);
        }
      }
    }, SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [trimmed]);

  const showResults: boolean = !!trimmed && results.length > 0;
  let status: string | null = null;
  if (!trimmed) status = '역 이름을 입력해 주세요.';
  else if (!showResults && (loading || searchedQuery !== trimmed)) status = '찾는 중이에요…';
  else if (failed) status = '역을 불러오지 못했어요. 잠시 후 다시 검색해 주세요.';
  else if (!showResults) status = `‘${trimmed}’ 역을 찾지 못했어요.`;

  return { results, status, loading };
};

/** 칩·결과 줄의 이름 — 같은 이름 다른 노선(서면 부산 1호선·2호선)을 구분한다 */
export const stationLabel = (station: SubwayStation): string =>
  station.lineNo ? `${station.name} ${station.lineNo}` : station.name;

const Status = styled.p`
  margin: 24px 0;
  text-align: center;
  font-size: 14px;
  color: ${COLOR.muted};
  word-break: keep-all;
`;

const Searching = styled.p`
  margin: 0 0 4px;
  font-size: 12px;
  color: ${COLOR.muted};
`;

const ResultList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const ResultRow = styled.button<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 52px;
  padding: 0 10px;
  border: none;
  border-bottom: 1px solid ${COLOR.lineSoft};
  border-radius: 0;
  background: ${({ selected }) => (selected ? COLOR.primarySoft : 'none')};
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  color: ${({ selected }) => (selected ? COLOR.primaryDeep : COLOR.text)};

  strong {
    font-size: 15px;
    font-weight: 700;
  }

  span {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 13px;
    color: ${COLOR.muted};
  }

  svg {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    color: ${COLOR.primary};
  }
`;

interface StationResultListProps {
  search: StationSearchState;
  selectedIds: Set<string>;
  onToggle: (station: SubwayStation) => void;
}

/** 검색 결과(역 이름 + 노선, 고른 역은 연청록 + ✓) 또는 안내 문구 */
export const StationResultList: React.FC<StationResultListProps> = ({ search, selectedIds, onToggle }) => {
  if (search.status) return <Status>{search.status}</Status>;
  return (
    <>
      {search.loading && <Searching>찾는 중이에요…</Searching>}
      <ResultList aria-label="역 검색 결과">
        {search.results.map((station: SubwayStation) => {
          const selected: boolean = selectedIds.has(station.id);
          return (
            <li key={station.id}>
              <ResultRow
                type="button"
                selected={selected}
                aria-pressed={selected}
                aria-label={stationLabel(station)}
                onClick={() => onToggle(station)}
              >
                <strong>{station.name}</strong>
                <span>{station.lineNo}</span>
                {selected && <CheckIcon aria-hidden />}
              </ResultRow>
            </li>
          );
        })}
      </ResultList>
    </>
  );
};
